import { Outlet, useParams } from "react-router-dom";
import { useFetch } from "../../../hooks/useFetch";
import { PluginContextProps } from "./models/PluginContextProps";
import { PluginResponse } from "../models/PluginResponse";
import { createContext, ReactElement, useCallback, useContext, useEffect, useState } from "react";
import { apiRoutes } from "../../../apiRoutes";
import { PluginDetailsExtended } from "../models/PluginDetailsExtended";

type PluginContextProviderProps = { children?: ReactElement };

const PluginContext = createContext<PluginContextProps>({
  pluginDetails: {} as PluginDetailsExtended,
  isLoading: true,
  categories: [],
  parentProducts: [],
  productDetails: [],
  manifestResult: {},
  setData: () => undefined,
  handleLoadingChange: () => undefined,
  getPlugin: async () => undefined,
  onManifestResultChange: () => undefined,
  setManifestResultField: () => undefined
});

export const usePlugin = () => useContext(PluginContext);

export const PluginContextProvider = ({ children }: PluginContextProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({} as PluginResponse);
  const [manifestResult, setManifestResult] = useState({});
  const { request } = useFetch();
  const { pluginId } = useParams() as { pluginId: string };

  const getPlugin = useCallback(
    async (id?: number) => {
      setIsLoading(true);
      await request({
        route: apiRoutes.pluginDetails.getPluginById(id ?? parseInt(pluginId)),
        onSuccess: async (response) => setData(await response.json()),
        onAny: () => setIsLoading(false)
      });
    },
    [request, pluginId]
  );

  useEffect(() => {
    getPlugin();
  }, [getPlugin]);

  const handleLoadingChange = (value: boolean) => {
    setIsLoading(value);
  };

  const onManifestResultChange = (result: { [key: string]: boolean }) => {
    setManifestResult(result);
  };

  const setManifestResultField = (key: string, value: boolean) => {
    setManifestResult((prev) => ({ ...prev, [key]: value }));
  };

  const categories = data?.categoryDetails?.map((x) => ({
    value: `${x.id}`,
    text: x.name
  }));

  const parentProducts = data?.parentProducts?.map((x) => ({
    value: `${x.id}`,
    text: x.productName
  }));

  return (
    <PluginContext.Provider
      value={{
        ...data,
        categories,
        parentProducts,
        isLoading,
        manifestResult,
        setData,
        onManifestResultChange,
        setManifestResultField,
        handleLoadingChange,
        getPlugin
      }}
    >
      {children ?? <Outlet />}
    </PluginContext.Provider>
  );
};
